import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type MqlQueueResult, type MqlSettingsResult } from '@whinerp/client';
import { formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * The MQL queue, where sales accepts or turns back what marketing sent (F-078).
 *
 * The threshold is on the queue itself: "both teams must see the same
 * threshold definition". A rep arguing that a lead is not qualified argues
 * with the number on this panel, which is the number marketing set.
 */
export function MqlQueuePanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-078');
  const [mine, setMine] = useState(true);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reasonId, setReasonId] = useState('');

  const queue = useQuery<MqlQueueResult, ApiError>({
    queryKey: ['mql-queue', mine],
    queryFn: () => api.mqlQueue({ query: { mine: mine ? 'true' : 'false' } }),
    enabled: enabled.enabled,
  });
  const settings = useQuery<MqlSettingsResult, ApiError>({
    queryKey: ['mql-settings'],
    queryFn: () => api.mqlSettings({}),
    enabled: enabled.enabled,
  });
  // No scheduler: overdue answers are escalated when somebody opens the queue.
  const escalate = useMutation({ mutationFn: () => api.escalateMqlBreaches({ body: {} }) });
  useEffect(() => {
    if (enabled.enabled) escalate.mutate();
    // Once per visit, not on every render of the queue.
  }, [enabled.enabled]);

  const decide = useMutation<unknown, ApiError, { leadId: string; decision: 'accept' | 'reject' }>({
    mutationFn: ({ leadId, decision }) =>
      api.decideMql({ path: { leadId }, body: decision === 'reject' ? { decision, reasonId: reasonId || null } : { decision } }),
    onSuccess: async () => {
      setRejecting(null);
      setReasonId('');
      await queryClient.invalidateQueries({ queryKey: ['mql-queue'] });
    },
  });

  if (!enabled.enabled) return null;
  const threshold = queue.data?.threshold ?? null;
  const reasons = (settings.data?.reasons ?? []).filter((reason) => reason.active);

  return (
    <Panel data-region="mql-queue">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <SectionLabel>{t('mql.queue')}</SectionLabel>
            <p data-mql-threshold className="text-meta text-ink-meta">
              {threshold === null ? t('mql.off') : t('mql.threshold', { values: { points: String(threshold) } })}
            </p>
          </div>
          <label className="flex items-center gap-2 text-dense text-ink">
            <input type="checkbox" data-mql-mine checked={mine} onChange={(event) => setMine(event.target.checked)} />
            {t('mql.onlyMine')}
          </label>
        </div>
        {decide.error ? (
          <p data-mql-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">{errorText(decide.error)}</p>
        ) : null}
        {queue.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : queue.isError ? (
          <p className="text-dense text-danger-text">{errorText(queue.error)}</p>
        ) : queue.data.leads.length === 0 ? (
          <p data-mql-empty className="text-meta text-ink-meta">{t('mql.empty')}</p>
        ) : (
          <ul className="flex flex-col">
            {queue.data.leads.map((lead) => (
              <li key={lead.leadId} data-mql-lead={lead.leadId} className="flex flex-col gap-2 border-t border-t-rule py-2 first:border-t-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/leads/${lead.leadId}`} className="min-w-0 flex-1 truncate text-body text-ink hover:underline">
                    {lead.company ? `${lead.name} · ${lead.company}` : lead.name}
                  </Link>
                  <span className="text-meta text-ink-meta">
                    {t('mql.scored', { values: { handoff: String(lead.scoreAtHandoff), now: String(lead.score) } })}
                  </span>
                  {lead.due ? (
                    <span data-mql-due className={lead.overdue ? 'text-meta text-danger-text' : 'text-meta text-ink-meta'}>
                      {t(lead.overdue ? 'mql.overdue' : 'mql.due', { values: { when: formatDateTime(lead.due, format) } })}
                    </span>
                  ) : null}
                </div>
                {rejecting === lead.leadId ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="flex flex-col gap-1 text-dense text-ink-600">
                      {t('mql.why')}
                      <select data-mql-reason className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink" value={reasonId} onChange={(event) => setReasonId(event.target.value)}>
                        <option value="">{t('mql.chooseReason')}</option>
                        {reasons.map((reason) => (
                          <option key={reason.reasonId} value={reason.reasonId}>{reason.label}</option>
                        ))}
                      </select>
                    </label>
                    <Button intent="danger" size="small" data-mql-confirm-reject disabled={decide.isPending} onClick={() => decide.mutate({ leadId: lead.leadId, decision: 'reject' })}>
                      {t('mql.reject')}
                    </Button>
                    <Button intent="ghost" size="small" onClick={() => setRejecting(null)}>{t('mql.cancel')}</Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button intent="primary" size="small" data-mql-accept disabled={decide.isPending} onClick={() => decide.mutate({ leadId: lead.leadId, decision: 'accept' })}>
                      {t('mql.accept')}
                    </Button>
                    <Button intent="ghost" size="small" data-mql-reject onClick={() => setRejecting(lead.leadId)}>
                      {t('mql.reject')}
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
